import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { ValueFormElement } from 'elements/_shared/formElement.js'
import { formFieldValidationTypes } from 'elements/form-field/form-field.js'
import { CSSResultGroup, html, nothing, PropertyValues } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { style } from './phone-number-input.style.js'
import {
  EXTERNAL_VALIDATION_SKIPPED,
  GOOGLE_LIBPHONENUMBER,
  GoogleLibphonenumber,
} from './google-libphonenumber.js'


initializeIconsByRole({
  'cse-phone-number-input.clear': { name: 'xmark', style: 'light' },
})

@customElement('cse-phone-number-input')
export class CSEPhoneNumberInput extends ValueFormElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @inject(GOOGLE_LIBPHONENUMBER)
  private googleLibphonenumber!: GoogleLibphonenumber

  @property({ type: String, reflect: true })
  valid: formFieldValidationTypes[number] = 'none'

  @state()
  private regionCodeForNumber?: string

  @property({ type: String, attribute: 'id' })
  _id = ''

  @property({ type: String })
  name = ''

  @property({ type: String, reflect: true })
  value = ''

  @property({ type: Boolean })
  required = false

  @property({ type: Boolean })
  disabled = false

  @property({ type: Boolean })
  clearable = false

  @property({ type: String })
  placeholder = ''

  @query('input')
  input!: HTMLInputElement

  @property({ type: String, attribute: 'required-text' })
  requiredText?: string

  @property({ type: String, attribute: 'invalid-text' })
  invalidText: string = 'This phone number is not valid'

  @property({ type: Boolean })
  override autofocus: boolean = false

  @property({
    type: Array,
    attribute: 'telephone-country-codes',
    converter: {
      fromAttribute: (value: unknown): string[] => {
        if (Array.isArray(value)) { return value as string[] }
        if (typeof value !== 'string') { return [] }
        try {
          const parsed = JSON.parse(value)
          if (Array.isArray(parsed)) { return parsed.map(String).map(code => code.toUpperCase()) }
        } catch { /* ignore and try split */ }
        return value
          .split(',')
          .map(v => v.trim())
          .filter(Boolean)
          .map(code => code.toUpperCase())
      },
    },
  })
  private telephoneCountryCodes: string[] = []

  override async connectedCallback() {
    super.connectedCallback()
    this.googleLibphonenumber.initialize(this.telephoneCountryCodes)

    if (this.name === '') {
      throw new Error('The name property must be set for cse-phone-number.')
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
  }

  protected override updated(changedProperties: PropertyValues) {
    super.updated(changedProperties)

    if (changedProperties.has('telephoneCountryCodes')) {
      this.googleLibphonenumber.initialize(this.telephoneCountryCodes)
    }

    if (['value', 'required'].some((prop: string) => changedProperties.has(prop))) {
      this.checkRequired()
    }
  }

  validateTelephoneField = (value: any): boolean => {
    const phoneNumberValidationResult = this.googleLibphonenumber.validatePhoneNumber(value)
    if (phoneNumberValidationResult !== EXTERNAL_VALIDATION_SKIPPED) {
      this.valid = phoneNumberValidationResult.isValidNumber ? 'none' : 'error'
      this.updateValidity()
      this.value = phoneNumberValidationResult.formatE164
      this.emit(this.value)
      this.regionCodeForNumber = phoneNumberValidationResult.regionCodeForNumber
      return phoneNumberValidationResult.isValidNumber
    }
    return this.telephoneFieldNotValidatedByGoogle(value)
  }

  telephoneFieldNotValidatedByGoogle = (value: any): boolean => {
    const raw = typeof value === 'string' ? value : ''
    const jankyFallbackValid = raw.length >= 5
    this.valid = jankyFallbackValid ? 'none' : 'error'
    this.updateValidity()
    this.value = raw
    this.regionCodeForNumber = ''
    return jankyFallbackValid
  }

  private updateValidity = () => {
    this.internals.setValidity(
      this.valid==='error' ? { badInput: true } : {},
      this.valid==='error' ? this.invalidText : undefined
    )
  }

  private onChange() {
    this.validateTelephoneField(this.input.value)
  }

  private onInput(event: Event) {
    if (!this.validity.badInput) return

    const target = event.currentTarget as { value?: string } | null
    const inputValue = (target && typeof target.value === 'string') ? target.value : ''
    this.validateTelephoneField(inputValue)
    this.checkRequired()
  }

  private checkRequired() {
    if (this.required && this.input.value.trim() === '') {
      this.internals.setValidity({
        valueMissing: true,
      }, this.requiredText || 'This field is required', this.input)
    } else {
      this.internals.setValidity({})
    }
  }

  private handleClearButtonClick = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()

    if (this.disabled) { return }

    this.input.value = ''
    this.value = ''
    this.valid = 'none'
    this.regionCodeForNumber = ''
    this.emit('')
    this.checkRequired()
    this.dispatchEvent(new CSEPhoneNumberInputClearedEvent(this))
  }

  private renderClearButton = () => html`
    <cse-icon-button
      variant="secondary"
      size="small"
      @click="${this.handleClearButtonClick}"
      aria-label="${t('clear_text', { ns: 'cse-phone-number-input', default: 'Clear text' })}"
    >
      <cse-icon icon-role="cse-phone-number-input.clear"></cse-icon>
    </cse-icon-button>
  `

  shouldRenderClearButton() {
    if (this.disabled || !this.clearable) {
      return false
    }

    return this.value !== '' && !this.required
  }

  override focus() {
    this.input.focus()
  }

  override render() {
    return html`
      <div class="${classMap({
        'cse-phone-number-input': true,
        [`valid-${this.valid}`]: true,
        'disabled': this.disabled,
      })}">
        <slot class="cse-phone-number-input-prefix" name="prefix"></slot>
        <input
          type="tel"
          id="${this._id}"
          name="${this.name}"
          value="${this.value}"
          ?required="${this.required}"
          ?disabled="${this.disabled}"
          ?autofocus="${this.autofocus}"
          placeholder="${this.placeholder}"
          @change="${this.onChange}"
          @input=${this.onInput}
        />
        <slot class="cse-phone-number-input-suffix" name="suffix">
          ${this.valid === 'none' && this.regionCodeForNumber
            ? html`${this.regionCodeForNumber}`
            : nothing
          }
        </slot>
        ${this.shouldRenderClearButton() ? this.renderClearButton() : null}
      </div>
    `
  }
}

export class CSEPhoneNumberInputClearedEvent extends Event {
  static type = 'cse-phone-number-input-cleared' as const

  public phoneNumberInput: CSEPhoneNumberInput

  constructor(phoneNumberInput: CSEPhoneNumberInput) {
    super(CSEPhoneNumberInputClearedEvent.type, { bubbles: true, composed: true })

    this.phoneNumberInput = phoneNumberInput
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cse-phone-number-input': CSEPhoneNumberInput
  }
  interface GlobalEventHandlersEventMap {
    [CSEPhoneNumberInputClearedEvent.type]: CSEPhoneNumberInputClearedEvent,
  }
}
